/* global reportModuleGalleryTiers reportModuleGallerySavedReports reportModuleGalleryModuleSwitcher */
/* global reportModuleGalleryModuleName reportModuleGalleryModuleKey reportModuleGalleryBuilderUrl reportModuleGalleryLandingUrl */

import React, { useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import { apiClient } from 'Helpers/network';

import Badge from 'CommonComponents/Badge';
import Button from 'CommonComponents/Button';

import { useToast } from 'Hooks/useToast';

/**
 * Report Module Gallery (IB-672).
 *
 * The click-through target for a builder-backed report group's landing card. It
 * renders, in the main content area (no second side rail), the module's
 * tier-grouped starting-point templates plus the user's own visibility-filtered
 * saved reports, a "Build Custom Report" card, a module switcher across the
 * builder-backed groups the user may see, and a breadcrumb back to the landing.
 *
 * Data is server-supplied via globals (the same payload the Blade fallback
 * renders), so the gallery is sourced from the module and never drifts. This is
 * a progressive enhancement: ReactDOM.render replaces the server-rendered
 * fallback subtree in #ReportModuleGallery once mounted.
 *
 * Module-agnostic — RADIO (IB-679) reuses it unchanged.
 */

const TIER_LABELS = {
    executive: 'Executive',
    management: 'Management',
    team: 'Team',
    linked: 'Linked',
};

const ReportModuleGallery = ({ tiers, savedReports: initialSavedReports, moduleSwitcher, moduleName, moduleKey, builderUrl, landingUrl }) => {
    const [savedReports, setSavedReports] = useState(initialSavedReports || []);
    const [deletingId, setDeletingId] = useState(null);
    const [duplicatingId, setDuplicatingId] = useState(null);
    const toast = useToast();

    const tierEntries = Object.entries(tiers || {}).filter(([, templates]) => Array.isArray(templates) && templates.length > 0);
    const switcherModules = moduleSwitcher || [];

    const templateUrl = (key) => `${builderUrl}?template=${encodeURIComponent(key)}`;

    const deleteSavedReport = (report) => {
        if (deletingId) {
            return;
        }

        setDeletingId(report.id);

        apiClient
            .delete(report.deleteUrl)
            .then(() => {
                setSavedReports((current) => current.filter((r) => r.id !== report.id));
                toast.success($t('Saved report deleted.'));
            })
            .catch(() => {
                toast.error($t('Failed to delete the saved report. Please try again later.'));
            })
            .finally(() => setDeletingId(null));
    };

    const duplicateSavedReport = (report) => {
        if (duplicatingId) {
            return;
        }

        setDuplicatingId(report.id);

        apiClient
            .post(report.duplicateUrl)
            .then(() => {
                toast.success($t('Saved report duplicated.'));
                // Reload so the server re-renders the gallery with the new copy
                // (visibility-filtered), rather than reconstructing the card here.
                window.location.reload();
            })
            .catch(() => {
                toast.error($t('Failed to duplicate the saved report. Please try again later.'));
                setDuplicatingId(null);
            });
    };

    return (
        <div className="tw-w-full">
            <nav aria-label={$t('Breadcrumb')} className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-charcoal-75 tw-mb-4">
                <a href={landingUrl} className="tw-text-blue hover:tw-underline">
                    {$t('Report Centre')}
                </a>
                <span aria-hidden="true">&rsaquo;</span>
                <span className="tw-font-medium tw-text-charcoal">{$t(moduleName)}</span>
            </nav>

            <div className="tw-flex tw-flex-col md:tw-flex-row md:tw-items-center md:tw-justify-between tw-gap-3 tw-mb-6">
                <h1 className="tw-text-xl tw-font-semibold tw-text-charcoal tw-m-0">{$t(moduleName)}</h1>

                {switcherModules.length > 0 ? (
                    <div className="tw-flex-shrink-0">
                        {/* Desktop: pills. Small screens: a dropdown. */}
                        <nav className="tw-hidden md:tw-flex tw-items-center tw-gap-2" aria-label={$t('Report groups')}>
                            {switcherModules.map((module) => {
                                const isActive = module.key === moduleKey;

                                return (
                                    <a
                                        key={module.key}
                                        href={module.url}
                                        aria-current={isActive ? 'page' : undefined}
                                        className={[
                                            'tw-px-3 tw-py-1.5 tw-rounded-full tw-text-sm tw-font-medium tw-transition-colors',
                                            isActive
                                                ? 'tw-bg-blue tw-text-white'
                                                : 'tw-bg-white tw-text-charcoal tw-border tw-border-grey hover:tw-border-blue',
                                        ].join(' ')}
                                    >
                                        {$t(module.name)}
                                    </a>
                                );
                            })}
                        </nav>
                        <select
                            className="md:tw-hidden tw-w-full tw-rounded-lg tw-border tw-border-grey tw-px-3 tw-py-2 tw-text-sm"
                            aria-label={$t('Report groups')}
                            value={moduleKey}
                            onChange={(e) => {
                                const target = switcherModules.find((m) => m.key === e.target.value);
                                if (target) {
                                    window.location.href = target.url;
                                }
                            }}
                        >
                            {switcherModules.map((module) => (
                                <option key={module.key} value={module.key}>
                                    {$t(module.name)}
                                </option>
                            ))}
                        </select>
                    </div>
                ) : null}
            </div>

            {tierEntries.map(([tier, templates]) => (
                <section key={tier} className="tw-mb-8">
                    <h2 className="tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-charcoal tw-mb-3">
                        <span className="tw-w-1 tw-h-3.5 tw-rounded tw-bg-blue" />
                        {$t(TIER_LABELS[tier] || tier)}
                    </h2>
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
                        {templates.map((template) => (
                            <TemplateCard
                                key={template.key}
                                title={$t(template.name)}
                                description={template.description ? $t(template.description) : ''}
                                href={templateUrl(template.key)}
                            />
                        ))}
                    </div>
                </section>
            ))}

            <section className="tw-mb-8">
                <h2 className="tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-charcoal tw-mb-3">
                    <span className="tw-w-1 tw-h-3.5 tw-rounded tw-bg-charcoal" />
                    {$t('Saved reports')}
                </h2>
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
                    <BuildCustomCard href={builderUrl} />
                    {savedReports.map((report) => (
                        <SavedReportCard
                            key={report.id}
                            report={report}
                            deleting={deletingId === report.id}
                            duplicating={duplicatingId === report.id}
                            onDelete={() => deleteSavedReport(report)}
                            onDuplicate={() => duplicateSavedReport(report)}
                        />
                    ))}
                </div>
            </section>
        </div>
    );
};

const CARD_CLASSES = 'tw-flex tw-flex-col tw-justify-between tw-gap-4 tw-p-4 tw-border tw-border-grey tw-rounded-xl tw-bg-white tw-h-full';

const TemplateCard = ({ title, description, href }) => (
    <a href={href} className={`${CARD_CLASSES} hover:tw-border-blue tw-transition-colors`}>
        <div className="tw-min-w-0">
            <div className="tw-font-semibold tw-text-charcoal">{title}</div>
            {description ? <div className="tw-text-sm tw-text-charcoal-75 tw-mt-1">{description}</div> : null}
        </div>
        <div className="tw-text-sm tw-font-medium tw-text-blue">{$t('Open')}</div>
    </a>
);

const BuildCustomCard = ({ href }) => (
    <a href={href} className={`${CARD_CLASSES} tw-border-dashed tw-items-center tw-justify-center tw-text-center hover:tw-border-blue tw-transition-colors`}>
        <div>
            <div className="tw-font-semibold tw-text-charcoal">{$t('Build Custom Report')}</div>
            <div className="tw-text-sm tw-text-charcoal-75 tw-mt-1">{$t('Start from an empty builder.')}</div>
        </div>
    </a>
);

const SavedReportCard = ({ report, deleting, duplicating, onDelete, onDuplicate }) => (
    <div className={CARD_CLASSES}>
        <div className="tw-min-w-0">
            <div className="tw-flex tw-items-center tw-gap-2">
                <span className="tw-font-semibold tw-text-charcoal tw-truncate">{report.name}</span>
                {report.isShared ? (
                    <Badge variant="neutral" size="md">
                        {$t('Shared')}
                    </Badge>
                ) : null}
            </div>
            {report.description ? <div className="tw-text-sm tw-text-charcoal-75 tw-mt-1">{report.description}</div> : null}
        </div>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <a href={report.openUrl} className="tw-text-sm tw-font-medium tw-text-blue hover:tw-underline">
                {$t('Open')}
            </a>
            <Button variant="secondary" small type="button" disabled={duplicating} onClick={onDuplicate}>
                {duplicating ? $t('Duplicating…') : $t('Duplicate')}
            </Button>
            {report.isOwner ? (
                <Button variant="secondary" small type="button" disabled={deleting} onClick={onDelete}>
                    {deleting ? $t('Deleting…') : $t('Delete')}
                </Button>
            ) : null}
        </div>
    </div>
);

export default ReportModuleGallery;

const rootEl = document.getElementById('ReportModuleGallery');

if (rootEl) {
    ReactDOM.render(
        <App>
            <ReportModuleGallery
                tiers={typeof reportModuleGalleryTiers !== 'undefined' ? reportModuleGalleryTiers : {}}
                savedReports={typeof reportModuleGallerySavedReports !== 'undefined' ? reportModuleGallerySavedReports : []}
                moduleSwitcher={typeof reportModuleGalleryModuleSwitcher !== 'undefined' ? reportModuleGalleryModuleSwitcher : []}
                moduleName={typeof reportModuleGalleryModuleName !== 'undefined' ? reportModuleGalleryModuleName : ''}
                moduleKey={typeof reportModuleGalleryModuleKey !== 'undefined' ? reportModuleGalleryModuleKey : ''}
                builderUrl={typeof reportModuleGalleryBuilderUrl !== 'undefined' ? reportModuleGalleryBuilderUrl : ''}
                landingUrl={typeof reportModuleGalleryLandingUrl !== 'undefined' ? reportModuleGalleryLandingUrl : ''}
            />
        </App>,
        rootEl,
    );
}
